import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth'; 
import Card from '../../common/Card';
import Badge from '../../common/Badge';
import Button from '../../common/Button';
import '../../../styles/Auction.css';

const AuctionSessionCard = ({ session, onEdit, onDelete }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    
    // State to hold the remaining time in milliseconds
    const [timeLeft, setTimeLeft] = useState(null);

    // useEffect hook to set up and clean up the countdown timer
    useEffect(() => {
        // Only run the timer if the session is active and has an end_time
        if (session.status !== 'active' || !session.end_time) {
            setTimeLeft(null);
            return;
        }

        const endDate = new Date(session.end_time).getTime();

        const calculateTimeLeft = () => {
            const now = new Date().getTime();
            const difference = endDate - now;

            if (difference > 0) {
                setTimeLeft(difference);
            } else {
                setTimeLeft(0); // Time's up
            }
        };

        // Initial calculation
        calculateTimeLeft();

        // Set up an interval to update every second
        const timerId = setInterval(calculateTimeLeft, 1000);

        // Cleanup function to clear the interval when the component unmounts
        return () => clearInterval(timerId);
    }, [session.end_time, session.status]); // Rerun effect if end_time or status changes

    // Helper function to format milliseconds into a readable HH:MM:SS or MM:SS string
    const formatTime = (milliseconds) => {
        if (!milliseconds) return '00:00';

        const totalSeconds = Math.floor(milliseconds / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (hours > 0) {
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        }
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const handleCardClick = () => {
        const route = user?.role === 'manager' 
            ? `/manager/dashboard/auction-room/${session.session_id}`
            : `/investor/auction/${session.session_id}`;
        
        navigate(route);
    };

    const getBadgeVariant = (status) => {
        switch (status) {
            case 'active':
                return 'live';
            case 'scheduled':
                return 'primary';
            case 'completed':
                return 'neutral';
            default:
                return 'neutral';
        }
    };

    return (
        <Card interactive={true} onClick={handleCardClick} className="auction-session-card">
            {/* --- Countdown Timer --- */}
            {session.status === 'active' && (
                <div className="countdown-timer">
                    Ends in: <span className="tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>{formatTime(timeLeft)}</span>
                </div>
            )}

            <div className='card-header'>
                <h4 className="floating-text floating-text-slow">{session.token_symbol || 'Unknown Token'}</h4>
                <Badge variant={getBadgeVariant(session.status)}>
                    {session.status === 'active' ? 'Live' : session.status}
                </Badge>
            </div>

            <div className='card-details'>
                <p>Starting Price: <strong className="tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>Rs {session.starting_price}</strong></p>
                {/* Conditionally show details that are only relevant after an auction */}
                {session.status === 'completed' && (
                    <>
                        <p>Final Price: <strong className="tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>Rs {session.final_price}</strong></p>
                        <p>Winner: <strong>{session.winner_email || 'N/A'}</strong></p>
                    </>
                )}
                <p>End Time: <strong className="tabular-nums">{new Date(session.end_time).toLocaleString()}</strong></p>
                <p>Duration: <strong className="tabular-nums">{session.duration_minutes} minutes</strong></p>
                <p>Created at: <strong className="tabular-nums">{session.created_at}</strong></p>
            </div>

            {(onEdit || onDelete) && session.status === 'pending' && (
                <div style={{ display: 'flex', gap: 8, padding: '8px 0', marginTop: 'auto', borderTop: '1px solid var(--border-hairline)' }}
                  onClick={(e) => e.stopPropagation()}>
                    {onEdit && (
                        <Button 
                            variant="secondary" 
                            size="sm" 
                            onClick={() => onEdit(session)} 
                            style={{ flex: 1 }}
                        >
                            Edit
                        </Button>
                    )}
                    {onDelete && (
                        <Button 
                            variant="danger" 
                            size="sm" 
                            onClick={() => onDelete(session)} 
                            style={{ flex: 1 }}
                        >
                            Delete
                        </Button>
                    )}
                </div>
            )}
        </Card>
    );
};

export default AuctionSessionCard;